<template>
	<AppDialog :dialogName="'Dynamic Data Mapper'" width="600">
		<template v-slot:header>
			<DialogHeader :service="service" :fieldItem="fieldItem" />
		</template>
		<div class="dynamic-data-mapper-dialog">
			<div class="dialog-title">1. Connect your own data</div>
			<div class="dialog-description">
				The mapping block lets users define their own data structure in setup. Start by defining your own data.
			</div>

			<div class="dialog-body">
				<DynamicParamsDispatcher
					:interfaceFieldUUID="interfaceFieldUUID"
					:action="action"
					:service="service"
					:params="params"
					@send-params-map="setParamsMap"
					@send-params-map-labels="setParamsMapLabels"
				/>
			</div>
			<div class="dialog-bottom">
				<SecondaryButton class="display-flex center-margin" @click="next" :disabled="!isFormFilled" :loading="isSaving">
					Next
				</SecondaryButton>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions, mapGetters } from 'vuex';
import AppDialog from '../AppDialog/AppDialog';
import WidgetService from '@/utils/WidgetService';
import ParamsService from '@/utils/ParamsService';
import { saveWidget } from '@/store/modules/widgets/actions';
import { getWidget } from '@/store/modules/widgets/getters';
import AppDialogManagerService from '../AppDialogManager/app-dialog-manager-service';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { saveConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/actions';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import DynamicParamsDispatcher from '@/components/BlockEditorWidget/WidgetFields/DynamicDataMapping/DynamicParamsDispatcher';
import DialogHeader from './common/DialogHeader';

import { FieldType } from '@/components/BlockEditorWidget/widgetFieldTypes.const';

export default {
	name: 'CreateMapperDialog',
	components: {
		AppDialog,
		DynamicParamsDispatcher,
		DialogHeader,
	},
	props: {
		// after creating new widget field in interface and configuration that the credentials are in
		action: { type: Object },
		service: { type: Object },
		interfaceFieldUUID: { type: String },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters([
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			getWidget.fullPath,
			getConfigurationFieldResponse.fullPath,
			editedWorkflowVersion.fullPath,
		]),
		...mapGetters('selectedNode', ['editNode']),
		node() {
			return this.editNode;
		},
		params() {
			return ParamsService.getParamsForMapping(this.service, this.action);
		},
		paramKeys() {
			return this.params.map((x) => x.key);
		},
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.interfaceFieldUUID);
		},
		configurationId() {
			return this.configurationAndInterfaceIdByFieldUUID.configurationId;
		},
		interfaceUUID() {
			return this.configurationAndInterfaceIdByFieldUUID.interfaceUUID;
		},
		interface() {
			return this.configurationAndInterfaceIdByFieldUUID.interface;
		},
		interfaceField() {
			return this.configurationAndInterfaceIdByFieldUUID.interface.fields?.find(
				(field) => field.field_type === FieldType.UserSchemaMappingsSelector && field.metadata.source.service === this.service.id
			);
		},
		fieldItem() {
			return this.interfaceField.metadata.source.field_item;
		},
		creatorMapResponse() {
			return this[getConfigurationFieldResponse.fullPath](this.configurationId, this.widgetFieldUUID) || {};
		},
		isFormFilled() {
			const paramsValues = Object.values(this.localParamsMap);
			const numOfRequiredParams = this.paramKeys.length;
			return paramsValues.length === numOfRequiredParams;
		},
	},
	methods: {
		...mapActions([saveConfigurationFieldResponse.fullPath, saveWidget.fullPath]),
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		goNext() {
			const { action, service, interfaceFieldUUID, fieldItem } = this;
			this.close();
			AppDialogManagerService.openTitleSelectionForMappingDialog({ action, service, interfaceFieldUUID, fieldItem });
		},
		setParamsMap(paramsMap) {
			this.$set(this, 'localParamsMap', paramsMap);
		},
		setParamsMapLabels(paramsMapLabels) {
			this.$set(this, 'localParamsMapLabels', paramsMapLabels);
		},
		async next() {
			const paramsMap = {}; // these are the fields that the user chose {key:value} ==> {solution_id:x, app_id:y}
			const paramsMapLabels = {};
			for (const key of Object.keys(this.localParamsMap)) {
				if (this.paramKeys.includes(key)) {
					paramsMap[key] = this.localParamsMap[key];
					paramsMapLabels[key] = this.localParamsMapLabels[key];
				}
			}
			if (Object.keys(paramsMap).length > 0) {
				await this.saveParamsMapInMapperWidgetField({ params_map: paramsMap, params_map_labels: paramsMapLabels });

				const value = { ...this.creatorMapResponse, params_map: paramsMap, params_map_labels: paramsMapLabels };
				await this[saveConfigurationFieldResponse.fullPath]({
					id: this.configurationId,
					interfaceUUID: this.interfaceUUID,
					fieldUUID: this.interfaceFieldUUID,
					response: value,
				});
			}
			this.goNext();
		},
		async saveParamsMapInMapperWidgetField(value) {
			const currentInterface = cloneDeep(this.interface);
			this.$set(this, 'isSaving', true);
			await WidgetService.saveParamsMapInMapperWidgetField(
				value,
				currentInterface,
				this[editedWorkflowVersion.fullPath]?.id,
				this.interfaceFieldUUID,
				this[saveWidget.fullPath]
			);
			this.$set(this, 'isSaving', false);
		},
	},
	data() {
		return {
			localParamsMap: {},
			localParamsMapLabels: {},
			isSaving: false,
		};
	},
};
</script>
